import React, { useState } from "react";

interface CreateRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ruleTypes = ["Fiat", "Crypto", "Other"];
const thresholdTypes = ["Amount", "Count"];

const CreateRuleModal: React.FC<CreateRuleModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [ruleName, setRuleName] = useState("");
  const [ruleType, setRuleType] = useState(ruleTypes[0]);
  const [thresholdType, setThresholdType] = useState(thresholdTypes[0]);
  const [triggerLogic, setTriggerLogic] = useState("");
  const [escalation, setEscalation] = useState("Alert Only");
  const [status, setStatus] = useState(true);
  const [notes, setNotes] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center font-manrope justify-center bg-black bg-opacity-80">
      <div className="bg-[#1F1F1F] rounded-2xl w-full max-w-lg p-0 shadow-lg relative">
        {/* Header */}
        <div className="flex justify-between items-center px-8 pt-8 pb-4">
          <h2 className="text-white text-2xl font-bold">Create new rule</h2>
          <button
            onClick={onClose}
            className="text-[#60B7F6] text-base font-semibold"
          >
            Close
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-8 pb-8 flex flex-col gap-4">
          <div>
            <label className="block text-[#AEBCC6] text-[15px] mb-2">
              Rule Name
            </label>
            <input
              type="text"
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
              className="w-full bg-[#1C2227] text-white border border-[#141414] rounded-lg px-4 py-2 outline-none "
              placeholder="Enter rule name"
              required
            />
          </div>
          <div>
            <label className="block text-[#AEBCC6] text-[15px] mb-2">
              Rule Type
            </label>
            <select
              value={ruleType}
              onChange={(e) => setRuleType(e.target.value)}
              className="w-full bg-[#1C2227] text-white border border-[#141414] rounded-lg px-4 py-2 outline-none "
              required
            >
              {ruleTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[#AEBCC6] text-[15px] mb-2">
              Threshold Type
            </label>
            <select
              value={thresholdType}
              onChange={(e) => setThresholdType(e.target.value)}
              className="w-full bg-[#1C2227] text-white border border-[#141414] rounded-lg px-4 py-2 outline-none "
              required
            >
              {thresholdTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[#AEBCC6] text-[15px] mb-2">
              Trigger Logic (Configurable)
            </label>
            <input
              type="text"
              value={triggerLogic}
              onChange={(e) => setTriggerLogic(e.target.value)}
              className="w-full bg-[#1C2227] text-white border border-[#141414] rounded-lg px-4 py-2 outline-none "
              placeholder="e.g. ₦5,000,000 in 24hrs"
              required
            />
          </div>
          <div>
            <label className="block text-[#AEBCC6] text-[15px] mb-2">
              Escalation
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                className={`flex-1 flex items-center justify-between px-4 gap-2 py-2 rounded-lg border ${
                  escalation === "Auto-Hold"
                    ? "border-[#1CA4F8] bg-[#181B1F] text-[#1CA4F8]"
                    : "border-[#23282D] bg-[#181B1F] text-[#AEBCC6]"
                }`}
                onClick={() => setEscalation("Auto-Hold")}
              >
                <span
                  className="w-4 h-4 rounded-full border flex items-center justify-center mr-2"
                  style={{
                    borderColor:
                      escalation === "Auto-Hold" ? "#1CA4F8" : "#828282",
                  }}
                >
                  {escalation === "Auto-Hold" && (
                    <span className="w-2 h-2 bg-[#1CA4F8] rounded-full block" />
                  )}
                </span>
                Auto-Hold
              </button>
              <button
                type="button"
                className={`flex-1 flex items-center justify-between px-4 gap-2 py-2 rounded-lg border ${
                  escalation === "Alert Only"
                    ? "border-[#1CA4F8] bg-[#181B1F] text-[#1CA4F8]"
                    : "border-[#23282D] bg-[#181B1F] text-[#AEBCC6]"
                }`}
                onClick={() => setEscalation("Alert Only")}
              >
                <span
                  className="w-4 h-4 rounded-full border flex items-center justify-center mr-2"
                  style={{
                    borderColor:
                      escalation === "Alert Only" ? "#1CA4F8" : "#828282",
                  }}
                >
                  {escalation === "Alert Only" && (
                    <span className="w-2 h-2 bg-[#1CA4F8] rounded-full block" />
                  )}
                </span>
                Alert Only
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[#AEBCC6] text-[15px] mb-2">
              Status
            </label>
            <div className="flex items-center justify-between w-full bg-[#1C2227] text-white border border-[#141414] rounded-lg px-4 py-2 outline-none">
              <span className="text-[#AEBCC6] text-[15px]">
                {status ? "Active" : "Inactive"}
              </span>
              <div
                onClick={() => setStatus((prev) => !prev)}
                className={`flex w-8  cursor-pointer h-4   rounded-full transition-all duration-500 ${
                  status ? "bg-secondary_button" : "bg-gray-600"
                }`}
              >
                <span
                  className={`h-4 w-4   rounded-full transition-all duration-500 bg-gray-100 ${
                    status ? "ml-4" : ""
                  }`}
                ></span>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[#AEBCC6] text-[15px] mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-[#1C2227] text-white border border-[#141414] rounded-lg px-4 py-2 outline-none min-h-[80px] resize-none"
              placeholder="Enter notes or description"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-[#1CA4F8] hover:bg-[#1890d5] text-white font-bold py-3 rounded-lg text-lg mt-2 transition-colors"
          >
            Create Rule
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateRuleModal;
